"use client";

import PlusMinusTransition from "@/components/icons/plus-minus-transition";
import { FooterNavigationEntry } from "@/components/layout/footer";
import TranslatedLink from "@/libs/base/components/translated-link";
import { cn } from "@/libs/base/utils/layout";
import { generateLinkTarget } from "@/libs/base/utils/link-utils";
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from "@headlessui/react";
import { useEffect } from "react";

interface PageProps {
  label: string;
  links: FooterNavigationEntry[];
}

const TOP_PADDING = 16;
const BOTTOM_PADDING = 16;
const ELEMENT_HEIGHT = 25;
const BORDER_HEIGHT = 1;

function calculateMaxHeightClass(subElementsLength: number) {
  return BORDER_HEIGHT + TOP_PADDING + subElementsLength * ELEMENT_HEIGHT + BOTTOM_PADDING + BORDER_HEIGHT;
}

export default function MobileFooterLinkDisclosure(props: PageProps) {
  const openClass = `footer-disclosure-${btoa(props.label).replaceAll("=", "")}-open`;

  const closeAnimationState = "transform scale-y-0 max-h-0";
  const openAnimationState = `transform scale-y-100 ${openClass}`;

  useEffect(() => {
    // Since HeadlessUI's Transition doesn't support transitioning between inline styles, we have to use classes.
    // And since TailwindCSS doesn't compile classes that use string interpolation, we'd have to have
    // a giant switch/case block for the different counts.
    // So instead of doing that we generate a style element so we don't have to manually calculate all different heights.
    const height = calculateMaxHeightClass(props.links.length);

    const el = document.createElement("style");
    el.innerHTML = `.${openClass} { max-height: ${height}px }`;
    document.body.appendChild(el);

    return () => {
      el.remove();
    };
  }, [openClass, props.label, props.links]);

  return (
    <Disclosure>
      {({ open }) => (
        <>
          <DisclosureButton className="relative w-full bg-lightgray-200 text-left">
            <div className="m-4 flex items-center justify-between gap-2">
              <span>{props.label}</span>
              <PlusMinusTransition
                as="span"
                className="pointer-events-none text-black"
                showMode={open ? "minus" : "plus"}
              />
            </div>

            <div
              className={cn(
                "border-b border-lightgray-300",
                "relative inset-x-0 bottom-0 h-0.5",
                "transition-all duration-100",
                !open && "mx-0",
                open && "mx-4",
              )}
            />
          </DisclosureButton>

          <Transition
            enter="transition-all origin-top duration-300"
            enterFrom={closeAnimationState}
            enterTo={openAnimationState}
            leave="transition-all origin-top duration-300"
            leaveFrom={openAnimationState}
            leaveTo={closeAnimationState}
          >
            <DisclosurePanel className="flex flex-col border-b border-lightgray-300 bg-lightgray-200 px-4 text-black">
              {props.links.map((link, index) => (
                <span key={link.label + index} className="first:pt-4 last:pb-4">
                  <TranslatedLink
                    href={link.href}
                    className="font-light hover:underline"
                    target={generateLinkTarget(link.openInNewTab)}
                  >
                    {link.label}
                  </TranslatedLink>
                </span>
              ))}
            </DisclosurePanel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}
